import { SignTransactionDAStep } from '@ledgerhq/device-signer-kit-ethereum'

/**
 * Should a failed `signTransaction` be retried once with clear signing turned
 * off (i.e. blind signed)?
 *
 * Extracted from the controller so it can be tested without a device: every
 * interesting case here is a (step, status word) pair that is impractical to
 * provoke on real hardware.
 *
 * The rule: a retry is only useful while the flow was still PREPARING the
 * signature. Once the device action reached `SIGN_TRANSACTION`, the Ethereum
 * app itself decided it could not sign - if that decision was 0x6985 it means
 * blind signing is disabled in the app's settings, and asking the very same
 * device a second time changes nothing. Retrying then would only look like a
 * phantom second signing request.
 */
export const shouldRetryWithBlindSigning = ({
  failedOnStep,
  errorMessage
}: {
  failedOnStep: string | null
  errorMessage?: unknown
}): boolean => {
  const hasReachedTheDevicePrompt =
    failedOnStep === SignTransactionDAStep.SIGN_TRANSACTION ||
    failedOnStep === SignTransactionDAStep.BLIND_SIGN_TRANSACTION_FALLBACK

  // Cancelling from the app (or the device getting unplugged) aborts the
  // subscription at whatever step it was on. Retrying would pop up a second
  // signing request out of nowhere. Note `Operation cancelled by user` is OUR
  // own string (see `#rejectSigningSubscription`), not a normalized device
  // message, which is why it is matched on the message rather than on a code.
  const wasAbortedByTheUserOrTheDevice =
    typeof errorMessage === 'string' &&
    (errorMessage.includes('Operation cancelled by user') || errorMessage.includes('disconnected'))

  return !hasReachedTheDevicePrompt && !wasAbortedByTheUserOrTheDevice
}
